import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, num, fmtDate, financialYear, fyBounds } from "@/lib/format";
import { useHasModule } from "@/context/AuthContext";
import { downloadFile } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Report catalogue. Each entry knows its endpoint, how to render itself, and
 * which export sections it offers — so adding a report is one object, not a
 * new page.
 */
const REPORTS = [
  { key: "gstr1", label: "GSTR-1", group: "GST returns", path: "/reports/gstr1",
    hint: "Outward supplies split the way the return is: B2B, B2C, credit notes, HSN and document series.",
    sections: [["b2b", "B2B invoices"], ["b2cs", "B2C (small)"], ["cdnr", "Credit notes"],
               ["hsn", "HSN summary"], ["documents", "Documents issued"]] },
  { key: "gstr3b", label: "GSTR-3B", group: "GST returns", path: "/reports/gstr3b",
    hint: "Summary return: outward liability against eligible input tax credit." },
  { key: "hsn-summary", label: "HSN summary", group: "GST returns", path: "/reports/hsn-summary",
    hint: "Table 12 on its own — quantity and value by HSN and rate." },
  { key: "tax-summary", label: "Tax summary", group: "Working papers", path: "/reports/tax-summary",
    hint: "Output tax less input tax, month by month." },
  { key: "sales-register", label: "Sales register", group: "Registers", path: "/reports/sales-register",
    exportPath: "/reports/sales-register/export" },
  { key: "purchase-register", label: "Purchase register", group: "Registers",
    path: "/purchases/reports/purchase-register", module: "purchases" },
  { key: "day-book", label: "Day book", group: "Registers", path: "/reports/day-book",
    hint: "Every money movement in date order." },
  { key: "receivables", label: "Receivables ageing", group: "Outstanding",
    path: "/reports/receivables-ageing", exportPath: "/reports/receivables-ageing/export" },
  { key: "payables", label: "Payables ageing", group: "Outstanding",
    path: "/purchases/reports/payables-ageing", module: "purchases" },
];

const money = (v) => inr(v);

export default function Reports() {
  const hasPurchases = useHasModule("purchases");
  const available = REPORTS.filter((r) => !r.module || (r.module === "purchases" && hasPurchases));
  const [active, setActive] = useState("gstr1");
  const [section, setSection] = useState("b2b");
  const [range, setRange] = useState(() => {
    const b = fyBounds(financialYear());
    return { from_date: b.from, to_date: b.to };
  });
  const [data, setData] = useState(null);
  // Which report the loaded data belongs to. Without this, switching reports
  // renders the new component against the previous payload for one frame and
  // throws on the first missing field.
  const [dataFor, setDataFor] = useState(null);
  const [loading, setLoading] = useState(false);

  const report = available.find((r) => r.key === active) || available[0];

  const load = useCallback(() => {
    if (!report) return;
    const forKey = report.key;
    setLoading(true);
    setData(null);
    setDataFor(null);
    api.get(report.path, { params: { from_date: range.from_date, to_date: range.to_date } })
      .then(({ data }) => { setData(data); setDataFor(forKey); })
      .catch((e) => { setData(null); setDataFor(null); toast.error(apiError(e)); })
      .finally(() => setLoading(false));
  }, [report, range]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (report?.sections) setSection(report.sections[0][0]); }, [report]);

  const doExport = async (format) => {
    const path = report.exportPath || report.path;
    try {
      await downloadFile(path, {
        format, from_date: range.from_date, to_date: range.to_date,
        ...(report.sections ? { section } : {}),
      }, `${report.key}.${format}`);
    } catch {
      toast.error("Could not generate that export");
    }
  };

  const groups = [...new Set(available.map((r) => r.group))];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Reports</h1>
        <p className="mt-1 text-sm text-slate-500">
          Statutory workings and registers, exportable to PDF, Excel or CSV
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        {groups.map((g) => (
          <div key={g}>
            <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-400">{g}</div>
            <div className="flex flex-wrap gap-1.5">
              {available.filter((r) => r.group === g).map((r) => (
                <button key={r.key} data-testid={`report-${r.key}`} onClick={() => setActive(r.key)}
                  className={`inline-flex min-h-[36px] items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wide transition-colors duration-150 sm:min-h-0 ${
                    active === r.key ? "border-slate-900 bg-slate-900 text-white"
                                     : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"
                  }`}>{r.label}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-white p-3">
        <div className="space-y-1">
          <Label className="text-[11px]">From</Label>
          <Input type="date" className="h-9 w-40" data-testid="report-from" value={range.from_date}
            onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label className="text-[11px]">To</Label>
          <Input type="date" className="h-9 w-40" data-testid="report-to" value={range.to_date}
            onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} />
        </div>
        {report?.sections && (
          <div className="space-y-1">
            <Label className="text-[11px]">Section to export</Label>
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger className="h-9 w-48 text-xs" data-testid="report-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                {report.sections.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="export-pdf"
            onClick={() => doExport("pdf")}><FileText size={14} /> PDF</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="export-xlsx"
            onClick={() => doExport("xlsx")}><FileSpreadsheet size={14} /> Excel</Button>
          <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="export-csv"
            onClick={() => doExport("csv")}><Download size={14} /> CSV</Button>
        </div>
      </div>

      {report?.hint && (
        <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-[11px] text-slate-600">
          <Info size={13} className="mt-0.5 shrink-0" /> {report.hint}
        </div>
      )}

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && data && dataFor === active && active === "gstr1" && <Gstr1 data={data} />}
      {!loading && data && dataFor === active && active === "gstr3b" && <Gstr3b data={data} />}
      {!loading && data && dataFor === active && active === "hsn-summary" && <Hsn data={data} />}
      {!loading && data && dataFor === active && active === "tax-summary" && <TaxSummary data={data} />}
      {!loading && data && dataFor === active && active === "day-book" && <DayBook data={data} />}
      {!loading && data && dataFor === active && active === "sales-register" && <SalesRegister data={data} />}
      {!loading && data && dataFor === active && active === "purchase-register" && <PurchaseRegister data={data} />}
      {!loading && data && dataFor === active && (active === "receivables" || active === "payables") &&
        <Ageing data={data} who={active === "receivables" ? "Customer" : "Supplier"} />}

      <p className="text-[11px] text-slate-500">
        These are working papers to file <em>from</em>, not a filing. Check them against the portal
        before you submit anything.
      </p>
    </div>
  );
}

/* ---------------- renderers ---------------- */
/* A statutory register is genuinely wide and restructuring it into cards
   would stop it reading like the return it mirrors. It scrolls sideways
   instead — which only works if people know it does, hence the hint. */
const ScrollHint = () => (
  <p className="px-3 pb-2 text-[11px] text-slate-400 md:hidden" data-testid="table-scroll-hint">
    Swipe the table sideways to see every column.
  </p>
);

const Table = ({ cols, rows, totals, testid, empty = "Nothing in this period" }) => (
  <div className="rounded-md border border-slate-200 bg-white">
    <ScrollHint />
    <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
        <tr>
          {cols.map((c) => (
            <th key={c.key} className={`px-3 py-2.5 font-semibold ${c.right ? "text-right" : "text-left"}`}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <tr key={i}>
            {cols.map((c) => (
              <td key={c.key} className={`px-3 py-2 ${c.right ? "text-right font-mono tabular-nums" : ""} ${c.mono ? "font-mono text-xs" : ""}`}>
                {c.render ? c.render(r) : (c.money ? money(r[c.key]) : r[c.key] ?? "—")}
              </td>
            ))}
          </tr>
        ))}
        {!rows.length && (
          <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-sm text-slate-500">{empty}</td></tr>
        )}
      </tbody>
      {totals && !!rows.length && (
        <tfoot>
          <tr className="bg-slate-900 text-white">
            {cols.map((c, i) => (
              <td key={c.key} className={`px-3 py-2.5 ${c.right ? "text-right font-mono font-bold" : "text-xs font-semibold uppercase tracking-widest"}`}>
                {i === 0 ? "Total" : (totals[c.key] != null ? money(totals[c.key]) : "")}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
    </div>
  </div>
);

const Gstr1 = ({ data }) => (
  <div className="space-y-4">
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {[["B2B", data.counts.b2b], ["B2C large", data.counts.b2cl], ["B2C small", data.counts.b2cs],
        ["Credit notes", data.counts.cdnr], ["HSN lines", data.hsn.length]].map(([l, v]) => (
        <div key={l} className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">{l}</div>
          <div className="mt-1 font-heading text-xl font-bold tabular-nums">{v}</div>
        </div>
      ))}
    </div>
    <Table testid="gstr1-b2b" rows={data.b2b} totals={data.totals.b2b} cols={[
      { key: "document_number", label: "Invoice", mono: true },
      { key: "document_date", label: "Date", render: (r) => fmtDate(r.document_date) },
      { key: "party_gstin", label: "GSTIN", mono: true },
      { key: "party_name", label: "Recipient" },
      { key: "place_of_supply", label: "Place of supply" },
      { key: "taxable_value", label: "Taxable", right: true, money: true },
      { key: "cgst", label: "CGST", right: true, money: true },
      { key: "sgst", label: "SGST", right: true, money: true },
      { key: "igst", label: "IGST", right: true, money: true },
      { key: "invoice_value", label: "Total", right: true, money: true },
    ]} empty="No B2B invoices in this period" />
  </div>
);

const Gstr3b = ({ data }) => {
  const o = data.outward, itc = data.itc_available, net = data.net_payable;
  const Row = ({ label, values, strong }) => (
    <tr className={strong ? "bg-slate-50 font-semibold" : ""}>
      <td className="px-4 py-2.5">{label}</td>
      {values.map((v, i) => (
        <td key={i} className="px-3 py-2.5 text-right font-mono tabular-nums">{money(v)}</td>
      ))}
    </tr>
  );
  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <ScrollHint />
      <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
      <table className="w-full text-sm" data-testid="gstr3b-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-2.5 text-left font-semibold">Particulars</th>
            <th className="px-3 py-2.5 text-right font-semibold">Taxable value</th>
            <th className="px-3 py-2.5 text-right font-semibold">IGST</th>
            <th className="px-3 py-2.5 text-right font-semibold">CGST</th>
            <th className="px-3 py-2.5 text-right font-semibold">SGST</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          <Row label="3.1(a) Outward taxable supplies" values={[o.taxable_value, o.igst, o.cgst, o.sgst]} />
          <Row label="3.1(c) Nil rated / exempt" values={[o.nil_rated_exempt, 0, 0, 0]} />
          <Row label="4(A) ITC available" values={[data.inward.taxable_value, itc.igst, itc.cgst, itc.sgst]} />
          <Row label="Net tax payable" values={[null, net.igst, net.cgst, net.sgst]} strong />
        </tbody>
        <tfoot>
          <tr className="bg-slate-900 text-white">
            <td colSpan={4} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-widest">
              Total payable this period
            </td>
            <td className="px-3 py-2.5 text-right font-mono font-bold" data-testid="gstr3b-net">
              {money(data.net_total)}
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
    </div>
  );
};

const Hsn = ({ data }) => (
  <Table testid="hsn-table" rows={data.rows} totals={data.totals} cols={[
    { key: "hsn_code", label: "HSN/SAC", mono: true },
    { key: "description", label: "Description" },
    { key: "uqc", label: "UQC" },
    { key: "rate", label: "Rate %", right: true, render: (r) => `${r.rate}%` },
    { key: "quantity", label: "Qty", right: true, render: (r) => num(r.quantity, 3) },
    { key: "taxable_value", label: "Taxable", right: true, money: true },
    { key: "cgst", label: "CGST", right: true, money: true },
    { key: "sgst", label: "SGST", right: true, money: true },
    { key: "igst", label: "IGST", right: true, money: true },
    { key: "total", label: "Total", right: true, money: true },
  ]} />
);

const TaxSummary = ({ data }) => (
  <Table testid="tax-summary-table" rows={data.rows} totals={data.totals} cols={[
    { key: "month_label", label: "Month" },
    { key: "sales_taxable", label: "Sales taxable", right: true, money: true },
    { key: "output_tax", label: "Output tax", right: true, money: true },
    { key: "purchase_taxable", label: "Purchase taxable", right: true, money: true },
    { key: "input_tax", label: "Input tax", right: true, money: true },
    { key: "net_tax", label: "Net payable", right: true, money: true },
  ]} />
);

const DayBook = ({ data }) => (
  <Table testid="day-book-table" rows={data.rows} totals={data.totals} cols={[
    { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
    { key: "type", label: "Type" },
    { key: "reference", label: "Reference", mono: true },
    { key: "party", label: "Party" },
    { key: "debit", label: "Debit", right: true, money: true },
    { key: "credit", label: "Credit", right: true, money: true },
  ]} />
);

const SalesRegister = ({ data }) => (
  <Table testid="sales-register-table" rows={data.rows || []} totals={data.totals} cols={[
    { key: "document_date", label: "Date", render: (r) => fmtDate(r.document_date) },
    { key: "document_number", label: "Invoice", mono: true },
    { key: "party_name", label: "Party" },
    { key: "party_gstin", label: "GSTIN", mono: true },
    { key: "taxable_value", label: "Taxable", right: true, money: true },
    { key: "grand_total", label: "Total", right: true, money: true },
  ]} />
);

const PurchaseRegister = ({ data }) => (
  <Table testid="purchase-register-table" rows={data.rows || []} totals={data.totals} cols={[
    { key: "bill_date", label: "Date", render: (r) => fmtDate(r.bill_date) },
    { key: "supplier_bill_number", label: "Bill", mono: true },
    { key: "supplier_name", label: "Supplier" },
    { key: "supplier_gstin", label: "GSTIN", mono: true },
    { key: "taxable_value", label: "Taxable", right: true, money: true },
    { key: "input_tax_credit", label: "ITC", right: true, money: true },
    { key: "total", label: "Total", right: true, money: true },
  ]} />
);

const Ageing = ({ data, who }) => {
  const buckets = data.buckets || [];
  return (
    <Table testid="ageing-table"
      rows={data.rows || []}
      totals={{ ...(data.totals || {}), total: data.grand_total }}
      cols={[
        { key: who === "Customer" ? "party_name" : "supplier_name", label: who },
        ...buckets.map((b) => ({ key: b, label: b, right: true, money: true })),
        { key: "total", label: "Total", right: true, money: true },
      ]}
      empty={who === "Customer" ? "Nothing outstanding" : "You owe nothing"} />
  );
};
